/**
 * R22-MVP — Cookie / Turnstile disclosure banner.
 *
 * Minimal, non-blocking banner shown at the bottom of the page on first visit.
 * Informs users about:
 *   - Essential session cookies (sr_session, sr_refresh)
 *   - Cloudflare Turnstile anti-bot verification
 *   - No tracking cookies, no advertising pixels
 *
 * Dismissal is stored in localStorage. Once dismissed, the banner never
 * reappears for that browser.
 *
 * testids:
 *   - cookie-banner
 *   - cookie-banner-accept
 *   - cookie-banner-learn-more
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const DISMISS_KEY = "sr-cookie-banner-dismissed";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) !== "1") {
        setVisible(true);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* */ }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[9998] bg-[#16161d]/95 backdrop-blur-md border-t border-white/10 px-4 py-3 sm:px-6 sm:py-4"
      data-testid="cookie-banner"
    >
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Cookie className="w-5 h-5 text-amber-400 shrink-0 mt-0.5 sm:mt-0" />
        <p className="text-xs text-white/70 leading-relaxed flex-1">
          Full Spectrum Center uses <span className="text-white/90 font-semibold">essential cookies only</span> for
          authentication (session &amp; refresh tokens) and Cloudflare Turnstile for bot protection.
          We do <span className="text-white/90 font-semibold">not</span> use tracking cookies, advertising pixels,
          or third-party analytics.{" "}
          <Link
            to="/legal/privacy#cookies"
            className="text-[#00F0FF] underline hover:opacity-80"
            data-testid="cookie-banner-learn-more"
          >
            Learn more
          </Link>
        </p>
        <button
          onClick={dismiss}
          className="shrink-0 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-[0.1em] text-white border border-white/10 transition-colors"
          data-testid="cookie-banner-accept"
        >
          Got it
        </button>
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 sm:static p-1 text-white/40 hover:text-white/80"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
